import * as PropTypes from "prop-types"
import styled, { css } from "styled-components"
import { useLocale } from "react-polyglot-hooks"
import { useRouter } from "next/router"
import { BreadcrumbJsonLd } from "next-seo"
import { getPath } from "helpers/getPath"
import { sendEvent } from "scripts/GoogleAnalytics"
import { color } from "helpers/styled"
import { Container } from "../../styled/Grid"
import Link from "../../Link"
import Icon from "../../icons/icon"
import { Home } from "../../icons/home"

const Wrapper = styled(Container)`
  position: relative;
  height: 25px;
  margin-bottom: 25px;
  z-index: 2;

  ${({ compactMobile }) =>
    compactMobile &&
    css`
      @media (max-width: 767px) {
        margin-bottom: 12px;
      }
    `}
`

const List = styled.ul`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;

  @media (max-width: 767px) {
    li:not(:first-child):not(:nth-child(2)):not(:nth-last-child(2)) {
      display: none;
    }
  }
`

const baseStyles = css`
  display: inline-flex;
  font-size: 11px;
  border-radius: 4px;
  min-width: 60px;
  text-align: center;
  margin-right: 5px;
  padding: 0 10px;
  align-items: center;
  min-height: 20px;
`

const Item = styled.a`
  ${baseStyles}
  color: ${({ theme, white }) => (white ? "white" : `${theme.colors.dimGray}CC`)};
  background: ${({ theme, white }) => (white ? "transparent" : theme.colors.lightGray)};
  border: 1px solid ${({ theme, white }) => (white ? "white" : theme.colors.lightGray)};
  transition:
    color 0.2s,
    border-color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.deepPink};
    border-color: ${({ theme }) => theme.colors.deepPink};
  }

  &:first-child {
    min-width: auto;
    padding: 0 5px;
  }
`

const CurrentItem = styled.span`
  ${baseStyles}
  color: ${color("silver")};
  background: transparent;
  border: 1px solid currentColor;
`

export default function Breadcrumbs({ page, items, itemsJsonLd, white, current, currentTitle, compactMobile }) {
  const locale = useLocale()
  const { asPath } = useRouter()
  const currentUrl = `https://swipcar.com${(asPath || "").split("?")[0]}`

  const handleClick = dest => {
    sendEvent("Breadcrumb", { event_category: page, event_label: dest })
  }

  const jsonItems = [
    { position: 1, name: "Swipcar", item: `https://swipcar.com/${locale}` },
    ...(itemsJsonLd || items).map((item, i) => ({
      position: i + 2,
      name: item.title || item.label,
      item: item.url || `https://swipcar.com/${locale}${getPath(locale, item.page, item.params)}`
    })),
    ...(current
      ? [
        {
          position: (itemsJsonLd || items).length + 2,
          name: currentTitle || current,
          item: currentUrl
        }
      ]
      : [])
  ]

  return (
    <>
      <BreadcrumbJsonLd itemListElements={jsonItems} />
      <Wrapper compactMobile={compactMobile}>
        <List>
          <li>
            <Link page="home">
              <Item white={white} onClick={() => handleClick("home")} title="Swipcar">
                <Icon Icon={Home} size="xs" />
              </Item>
            </Link>
          </li>
          {items.map(item => (
            <li key={item.page}>
              <Link page={item.page} params={item.params}>
                <Item white={white} onClick={() => handleClick(item.page)} title={item.title || item.label}>
                  {item.label}
                </Item>
              </Link>
            </li>
          ))}
          {current && (
            <li>
              <CurrentItem title={currentTitle || current}>{current}</CurrentItem>
            </li>
          )}
        </List>
      </Wrapper>
    </>
  )
}

Breadcrumbs.defaultProps = {
  white: false,
  current: null,
  currentTitle: null,
  compactMobile: false
}

Breadcrumbs.propTypes = {
  page: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  // eslint-disable-next-line react/require-default-props
  itemsJsonLd: PropTypes.arrayOf(PropTypes.object),
  white: PropTypes.bool,
  current: PropTypes.string,
  currentTitle: PropTypes.string,
  compactMobile: PropTypes.bool
}
